// Zod schemas mirroring apm/app/schemas.py.
// Inferred TS types are exported alongside the schemas so callers can use
// either at the type level (`User`) or for runtime validation (`UserSchema`).
//
// Datetime/date fields stay as ISO strings — formatting is the view's job.

import { z } from 'zod';

export const UserSchema = z.object({
  id: z.number(),
  name: z.string(),
  email: z.string(),
  role: z.string(),
  function: z.string().nullable().optional(),
  is_active: z.boolean().default(true),
  has_password: z.boolean().default(false),
  last_login_at: z.string().nullable().optional(),
});
export type User = z.infer<typeof UserSchema>;

// Self-view of the signed-in user. Extends UserSchema with personal_context
// (private to that user — never returned in the all-users list). Used by
// /api/auth/me only.
export const MeSchema = UserSchema.extend({
  personal_context: z.string().nullable().optional(),
});
export type Me = z.infer<typeof MeSchema>;

export const ProjectGateSchema = z.object({
  id: z.number(),
  gate_name: z.string(),
  gate_order: z.number(),
  required: z.boolean(),
  reviewer_id: z.number().nullable().optional(),
  outcome: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  completed_at: z.string().nullable().optional(),
});
export type ProjectGate = z.infer<typeof ProjectGateSchema>;

export const ProjectTechSchema = z.object({
  languages: z.array(z.string()).default([]),
  external_systems: z.array(z.string()).default([]),
  internal_systems: z.array(z.string()).default([]),
  hosting: z.string().nullable().optional(),
  auth_method: z.string().nullable().optional(),
  secrets_method: z.string().nullable().optional(),
  data_storage: z.string().nullable().optional(),
  cost_estimate: z.string().nullable().optional(),
});
export type ProjectTech = z.infer<typeof ProjectTechSchema>;

export const ProjectSourceSchema = z.object({
  id: z.number(),
  source_name: z.string(),
  source_type: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
});
export type ProjectSource = z.infer<typeof ProjectSourceSchema>;

export const ProjectProgressSchema = z.object({
  id: z.number(),
  project_id: z.number(),
  entry_date: z.string(),
  note: z.string(),
  author: UserSchema.nullable().optional(),
  created_at: z.string(),
});
export type ProjectProgress = z.infer<typeof ProjectProgressSchema>;

export const ProjectSchema = z.object({
  id: z.number(),
  name: z.string(),
  status: z.string(),
  owner: UserSchema.nullable().optional(),
  sponsor: UserSchema.nullable().optional(),
  developer: UserSchema.nullable().optional(),
  goal: z.string().nullable().optional(),
  business_case: z.string().nullable().optional(),
  user_base_type: z.string().nullable().optional(),
  user_base_size: z.number().nullable().optional(),
  involves_phi_pii: z.boolean(),
  phi_pii_description: z.string().nullable().optional(),
  recommended_hosting: z.string().nullable().optional(),
  hosting_rationale: z.string().nullable().optional(),
  hosting_override_reason: z.string().nullable().optional(),
  target_go_live: z.string().nullable().optional(),
  actual_go_live: z.string().nullable().optional(),
  proposal_date: z.string().nullable().optional(),
  sharepoint_url: z.string().nullable().optional(),
  priority_score: z.number().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  gates: z.array(ProjectGateSchema).default([]),
  tech: ProjectTechSchema.nullable().optional(),
  sources: z.array(ProjectSourceSchema).default([]),
  component_ids: z.array(z.number()).default([]),
  progress_entries: z.array(ProjectProgressSchema).default([]),
});
export type Project = z.infer<typeof ProjectSchema>;

export const InitiativeSchema = z.object({
  id: z.number(),
  // project_id is the legacy "primary" project — nullable for standalone
  // initiatives. The full set is project_ids + project_names.
  project_id: z.number().nullable().optional(),
  project_ids: z.array(z.number()).default([]),
  project_names: z.array(z.string()).default([]),
  name: z.string(),
  description: z.string().nullable().optional(),
  target_completion: z.string().nullable().optional(),
  status: z.string(),
  color: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  // Tickets — counts by status bucket.
  ticket_count: z.number().default(0),
  done_count: z.number().default(0),
  in_progress_count: z.number().default(0),
  not_started_count: z.number().default(0),
  // Projects — same shape, populated from the M:N junction.
  project_count: z.number().default(0),
  projects_done_count: z.number().default(0),
  projects_in_progress_count: z.number().default(0),
  projects_not_started_count: z.number().default(0),
  // Weighted progress across both items, 0–100. See backend
  // _rollup_projects / _rollup_tickets for partial-credit rules.
  done_percent: z.number().default(0),
});
export type Initiative = z.infer<typeof InitiativeSchema>;

export const ReleaseSchema = z.object({
  id: z.number(),
  project_id: z.number(),
  version: z.string(),
  name: z.string().nullable().optional(),
  target_date: z.string().nullable().optional(),
  actual_date: z.string().nullable().optional(),
  status: z.string(),
  release_notes: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
  ticket_count: z.number().default(0),
  done_count: z.number().default(0),
  in_progress_count: z.number().default(0),
  not_started_count: z.number().default(0),
});
export type Release = z.infer<typeof ReleaseSchema>;

export const InviteSchema = z.object({
  id: z.number(),
  email: z.string(),
  name: z.string().nullable().optional(),
  role: z.string(),
  created_at: z.string(),
  expires_at: z.string(),
  accepted_at: z.string().nullable().optional(),
  invited_by: UserSchema.nullable().optional(),
});
export type Invite = z.infer<typeof InviteSchema>;

export const SentLinkSchema = z.object({
  email: z.string(),
  email_sent: z.boolean(),
  link: z.string().nullable().optional(),
  expires_at: z.string(),
});
export type SentLink = z.infer<typeof SentLinkSchema>;

export const OrgSystemSchema = z.object({
  id: z.number(),
  name: z.string(),
  category: z.string(),
  notes: z.string().nullable().optional(),
});
export type OrgSystem = z.infer<typeof OrgSystemSchema>;

export const IdeaSchema = z.object({
  id: z.number(),
  title: z.string(),
  description: z.string().nullable().optional(),
  submitter: UserSchema.nullable().optional(),
  owner: UserSchema.nullable().optional(),
  status: z.string(),
  promoted_project_id: z.number().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Idea = z.infer<typeof IdeaSchema>;

// Search results — minimal per-row metadata enough to render in the dropdown
// without follow-up fetches. Each entity type returns a `total` count plus
// a capped list of `results`.
const ProjectHitSchema = z.object({
  id: z.number(),
  name: z.string(),
  status: z.string(),
  owner_name: z.string().nullable().optional(),
  involves_phi_pii: z.boolean(),
});
const InitiativeHitSchema = z.object({
  id: z.number(),
  name: z.string(),
  project_id: z.number(),
  project_name: z.string().nullable().optional(),
  status: z.string(),
});
const IdeaHitSchema = z.object({
  id: z.number(),
  title: z.string(),
  status: z.string(),
  submitter_name: z.string().nullable().optional(),
});
const TicketHitSchema = z.object({
  id: z.number(),
  title: z.string(),
  project_id: z.number(),
  project_name: z.string().nullable().optional(),
  status: z.string(),
  priority: z.string(),
  type: z.string(),
});
const ComponentHitSchema = z.object({
  id: z.number(),
  name: z.string(),
  type: z.string(),
  status: z.string(),
  phi_safe: z.boolean(),
});
function groupSchema<T extends z.ZodTypeAny>(t: T) {
  return z.object({ total: z.number(), results: z.array(t) });
}
export const SearchResultsSchema = z.object({
  query: z.string(),
  projects: groupSchema(ProjectHitSchema),
  initiatives: groupSchema(InitiativeHitSchema),
  ideas: groupSchema(IdeaHitSchema),
  tickets: groupSchema(TicketHitSchema),
  components: groupSchema(ComponentHitSchema),
});
export type SearchResults = z.infer<typeof SearchResultsSchema>;
export type ProjectHit = z.infer<typeof ProjectHitSchema>;
export type InitiativeHit = z.infer<typeof InitiativeHitSchema>;
export type IdeaHit = z.infer<typeof IdeaHitSchema>;
export type TicketHit = z.infer<typeof TicketHitSchema>;
export type ComponentHit = z.infer<typeof ComponentHitSchema>;

// Full Component record from /api/components — different from ComponentHit
// used in search results, which only has summary fields.
export const ComponentSchema = z.object({
  id: z.number(),
  name: z.string(),
  description: z.string().nullable().optional(),
  type: z.string(),
  owner: UserSchema.nullable().optional(),
  status: z.string(),
  phi_safe: z.boolean(),
  repo_link: z.string().nullable().optional(),
  sharepoint_url: z.string().nullable().optional(),
  version: z.string().nullable().optional(),
  notes: z.string().nullable().optional(),
  claude_resource_type: z.string().nullable().optional(),
  claude_resource_id: z.string().nullable().optional(),
  claude_workspace_id: z.string().nullable().optional(),
  claude_console_url: z.string().nullable().optional(),
  spec: z.unknown().nullable().optional(),
  risk_tier: z.string().nullable().optional(),
  last_tested_at: z.string().nullable().optional(),
  last_deployed_at: z.string().nullable().optional(),
  next_review_due_at: z.string().nullable().optional(),
  project_ids: z.array(z.number()).default([]),
  updated_at: z.string(),
});
export type Component = z.infer<typeof ComponentSchema>;

export const ChatMessageSchema = z.object({
  id: z.number(),
  session_id: z.number(),
  role: z.string(),                        // user | assistant | tool_result
  text: z.string().nullable().optional(),
  tool_call: z.unknown().nullable().optional(),
  tool_result: z.unknown().nullable().optional(),
  tool_status: z.string().nullable().optional(),  // pending | applied | cancelled
  scope_type: z.string().nullable().optional(),
  scope_id: z.number().nullable().optional(),
  applied_by: UserSchema.nullable().optional(),
  applied_at: z.string().nullable().optional(),
  created_at: z.string(),
});
export type ChatMessage = z.infer<typeof ChatMessageSchema>;

export const ChatSessionSchema = z.object({
  id: z.number(),
  user_id: z.number().nullable().optional(),
  scope_type: z.string(),
  scope_id: z.number(),
  mode: z.string().default('default'),
  created_at: z.string(),
  updated_at: z.string(),
  messages: z.array(ChatMessageSchema).default([]),
});
export type ChatSession = z.infer<typeof ChatSessionSchema>;

export const ChatSendOutSchema = z.object({
  session_id: z.number(),
  new_messages: z.array(ChatMessageSchema).default([]),
});
export type ChatSendOut = z.infer<typeof ChatSendOutSchema>;

export const ChatApplyOutSchema = z.object({
  message: ChatMessageSchema,
  followup: z.array(ChatMessageSchema).default([]),
});
export type ChatApplyOut = z.infer<typeof ChatApplyOutSchema>;

export const AuditEntrySchema = z.object({
  id: z.number(),
  timestamp: z.string(),
  user_id: z.number().nullable().optional(),
  user_name: z.string().nullable().optional(),
  user_email: z.string().nullable().optional(),
  action: z.string(),
  entity_type: z.string().nullable().optional(),
  entity_id: z.number().nullable().optional(),
  details: z.unknown().nullable().optional(),
});
export type AuditEntry = z.infer<typeof AuditEntrySchema>;

export const ProjectFileSchema = z.object({
  id: z.number(),
  project_id: z.number(),
  filename: z.string(),
  version: z.number(),
  size_bytes: z.number().nullable().optional(),
  content_type: z.string().nullable().optional(),
  description: z.string().nullable().optional(),
  source: z.string().nullable().optional(),
  uploader: UserSchema.nullable().optional(),
  created_at: z.string(),
});
export type ProjectFile = z.infer<typeof ProjectFileSchema>;

export const ProjectFileLatestSchema = z.object({
  filename: z.string(),
  latest_version: z.number(),
  total_versions: z.number(),
  latest: ProjectFileSchema,
});
export type ProjectFileLatest = z.infer<typeof ProjectFileLatestSchema>;

export const ProjectRepositorySchema = z.object({
  id: z.number(),
  project_id: z.number(),
  kind: z.string(),
  url: z.string(),
  label: z.string().nullable().optional(),
  is_primary: z.boolean().default(false),
  notes: z.string().nullable().optional(),
  last_verified_at: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type ProjectRepository = z.infer<typeof ProjectRepositorySchema>;

export const AgentToolSchema = z.object({
  name: z.string(),
  description: z.string(),
  mutating: z.boolean(),
});
export type AgentTool = z.infer<typeof AgentToolSchema>;

export const AgentConfigSchema = z.object({
  agent_name: z.string(),
  system_prompt: z.string(),
  description: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
  updated_by: UserSchema.nullable().optional(),
  tools: z.array(AgentToolSchema).default([]),
});
export type AgentConfig = z.infer<typeof AgentConfigSchema>;

export const NotificationSchema = z.object({
  id: z.number(),
  created_at: z.string(),
  subject: z.string(),
  body: z.string().nullable().optional(),
  channel: z.string(),
  read: z.boolean(),
  related_entity: z.string().nullable().optional(),
  related_id: z.number().nullable().optional(),
});
export type Notification = z.infer<typeof NotificationSchema>;

export const HostingRecResponseSchema = z.object({
  recommendation: z.string(),
  rationale: z.string(),
  warnings: z.array(z.string()).default([]),
  additional_steps: z.array(z.string()).default([]),
});
export type HostingRecResponse = z.infer<typeof HostingRecResponseSchema>;

export const HostingRecHistorySchema = z.object({
  id: z.number(),
  project_id: z.number(),
  recommendation: z.string(),
  rationale: z.string().nullable().optional(),
  warnings: z.array(z.string()).default([]),
  additional_steps: z.array(z.string()).default([]),
  generated_at: z.string(),
  overridden: z.boolean(),
  override_reason: z.string().nullable().optional(),
});
export type HostingRecHistory = z.infer<typeof HostingRecHistorySchema>;

export const TicketSchema = z.object({
  id: z.number(),
  project_id: z.number(),
  type: z.string(),
  title: z.string(),
  description: z.string().nullable().optional(),
  submitter: UserSchema.nullable().optional(),
  assignee: UserSchema.nullable().optional(),
  priority: z.string(),
  status: z.string(),
  effort: z.string().nullable().optional(),
  due_date: z.string().nullable().optional(),
  initiative_id: z.number().nullable().optional(),
  release_id: z.number().nullable().optional(),
  parent_ticket_id: z.number().nullable().optional(),
  owner_response: z.string().nullable().optional(),
  created_at: z.string(),
  updated_at: z.string(),
});
export type Ticket = z.infer<typeof TicketSchema>;
